import {
  AUTH_LOCALE_INTENT_COOKIE_MAX_AGE_SECONDS,
  AUTH_LOCALE_INTENT_COOKIE_NAME,
  LOCALE_COOKIE_MAX_AGE_SECONDS,
  LOCALE_COOKIE_NAME,
  normalizeLocale,
  type SupportedLocale,
} from '@/i18n/config';

const PENDING_SIGNUP_LOCALE_STORAGE_KEY = 'auth.pendingSignupLocale';

export function resolveAuthFlowLocale({
  localeParam,
  pendingLocale,
  currentLocale,
}: {
  localeParam: string | null | undefined;
  pendingLocale: string | null | undefined;
  currentLocale: string | null | undefined;
}): SupportedLocale {
  return normalizeLocale(localeParam ?? pendingLocale ?? currentLocale);
}

function setBrowserCookie(
  name: string,
  value: string,
  maxAge: number = LOCALE_COOKIE_MAX_AGE_SECONDS
): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function clearBrowserCookie(name: string): void {
  setBrowserCookie(name, '', 0);
}

function getBrowserCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const prefix = `${name}=`;
  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
}

export function setLocaleCookie(locale: string): SupportedLocale {
  const normalizedLocale = normalizeLocale(locale);

  setBrowserCookie(LOCALE_COOKIE_NAME, normalizedLocale);

  return normalizedLocale;
}

export function getPendingSignupLocale(): SupportedLocale | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storedLocale = window.localStorage.getItem(
      PENDING_SIGNUP_LOCALE_STORAGE_KEY
    );

    return storedLocale
      ? normalizeLocale(storedLocale)
      : getPendingAuthLocaleCookie();
  } catch {
    return getPendingAuthLocaleCookie();
  }
}

export function getPendingAuthLocaleCookie(): SupportedLocale | null {
  const locale = getBrowserCookie(AUTH_LOCALE_INTENT_COOKIE_NAME);

  return locale ? normalizeLocale(locale) : null;
}

export function setPendingSignupLocale(locale: string): SupportedLocale {
  const normalizedLocale = setLocaleCookie(locale);
  setBrowserCookie(
    AUTH_LOCALE_INTENT_COOKIE_NAME,
    normalizedLocale,
    AUTH_LOCALE_INTENT_COOKIE_MAX_AGE_SECONDS
  );

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(
        PENDING_SIGNUP_LOCALE_STORAGE_KEY,
        normalizedLocale
      );
    } catch {}
  }

  return normalizedLocale;
}

export function clearPendingSignupLocale(
  fallbackLocale?: string | null
): SupportedLocale | null {
  if (typeof window === 'undefined') {
    return fallbackLocale ? normalizeLocale(fallbackLocale) : null;
  }

  try {
    window.localStorage.removeItem(PENDING_SIGNUP_LOCALE_STORAGE_KEY);
  } catch {}

  clearBrowserCookie(AUTH_LOCALE_INTENT_COOKIE_NAME);

  if (!fallbackLocale) {
    return null;
  }

  return setLocaleCookie(fallbackLocale);
}
